// services/streamingMarketData.js
//
// Server-Sent Events (SSE) client for the AI Trading Agents → Live Market
// Data tab. Connects to GET /api/trade-execution/ai-agents/:id/market-data/stream
// and pushes live tick + 30-second-bar events back to the caller.
//
// Why SSE (and not WebSocket): the backend prod server is Waitress (WSGI-only)
// which doesn't support WebSocket protocol upgrades. SSE is plain HTTP and
// works through Waitress, nginx, etc. unchanged. The browser's `EventSource`
// auto-reconnects on drop. We only need server → client push (live trades
// from Alpaca → UI), so SSE is the right protocol.
//
// Why event-source-polyfill: the standard browser `EventSource` does NOT
// support custom headers (Authorization, X-Active-Team-ID). The polyfill
// adds header support so we can reuse the same JWT-based auth as every
// other API call without putting tokens in the URL.

import { EventSourcePolyfill } from 'event-source-polyfill';
import backendURL from '../config';

/**
 * Open a streaming connection for an agent's live market data.
 *
 * @param {number|string} agentId
 * @param {object} handlers
 * @param {(snap: object) => void} [handlers.onSnapshot]   first frame: current price + VWAP
 * @param {(tick: object) => void} [handlers.onTick]       per-trade tick: {price, ts, vwap_*}
 * @param {(bar: object) => void}  [handlers.onBar30s]     closed 30-second OHLCV bar
 * @param {(err: object) => void}  [handlers.onError]      transport / server error
 * @returns {{ close: () => void }}
 */
export function openLiveMarketDataStream(agentId, handlers = {}) {
    const { onSnapshot, onTick, onBar30s, onError } = handlers;

    let token = '';
    let teamId = '';
    try {
        const stored = localStorage.getItem('user');
        if (stored) {
            const u = JSON.parse(stored);
            token = u?.access_token || '';
            teamId = u?.active_team?.team_id ?? '';
        }
    } catch (e) {
        // proceed without auth headers; the backend will return 403 and the
        // onError handler will fire so the UI can react
    }

    const url = `${backendURL}/api/trade-execution/ai-agents/${agentId}/market-data/stream`;

    const es = new EventSourcePolyfill(url, {
        headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'X-Active-Team-ID': String(teamId || ''),
        },
        // Polyfill-only options — browser timeout for the silent stream.
        // We send `: keepalive` comments every 15s server-side; bumping this
        // a bit higher avoids spurious reconnects on slow networks.
        heartbeatTimeout: 45_000,
    });

    es.onmessage = (e) => {
        if (!e?.data) return;
        let parsed;
        try {
            parsed = JSON.parse(e.data);
        } catch (err) {
            // Likely a non-JSON heartbeat; ignore
            return;
        }
        if (!parsed || !parsed.type) return;

        switch (parsed.type) {
            case 'snapshot':
                onSnapshot?.(parsed);
                break;
            case 'tick':
                onTick?.(parsed);
                break;
            case 'bar_30s':
                onBar30s?.(parsed);
                break;
            case 'error':
                onError?.(parsed);
                break;
            default:
                // Unknown event type — ignore (forward-compat)
                break;
        }
    };

    es.onerror = (err) => {
        // The polyfill auto-reconnects unless we explicitly close. Surface the
        // error to the caller so it can show a "Stale" indicator while the
        // reconnect is in flight.
        onError?.({ type: 'transport', error: err });
    };

    return {
        close: () => {
            try {
                es.close();
            } catch (e) {
                // no-op
            }
        },
    };
}

/**
 * Convert a TimeFrame enum string (e.g. "FIVE_MIN") to seconds. Used to
 * compute the user-TF chart's bucket size for live in-progress bar updates.
 */
export function timeframeToSeconds(tf) {
    switch (tf) {
        case 'THIRTY_SEC': return 30;
        case 'ONE_MIN':    return 60;
        case 'TWO_MIN':    return 120;
        case 'THREE_MIN':  return 180;
        case 'FOUR_MIN':   return 240;
        case 'FIVE_MIN':   return 300;
        case 'FIFTEEN_MIN':return 900;
        case 'ONE_HOUR':   return 3600;
        case 'TWO_HOUR':   return 7200;
        case 'FOUR_HOUR':  return 14400;
        case 'FIVE_HOUR':  return 18000;
        case 'DAILY':      return 86400;
        case 'WEEKLY':     return 604800;
        case 'MONTHLY':    return 2592000;
        default:           return 300;  // 5-min default
    }
}
